import { isEmpty, isNil, uniqBy } from 'lodash'

import UsersApi from '@/api/workspace-users'
import {
  OBJECTIVES_ADD_USERS,
  OBJECTIVES_SET_ALL_WORKSPACES_LEVELS,
  OBJECTIVES_SET_FULL_LEVELS_LIST,
  OBJECTIVES_SET_LEVELS,
  OBJECTIVES_SET_SORTING,
  SET_ELEMENT_FOR_EDIT
} from '@/store/mutation-types'
import { OKR_LEVEL_ENTITY_KEYS, REQUEST_ENTITY_KEYS } from '@/utils/entity-keys'
import { OBJECTIVE_TYPES } from '@/utils/objective-types'

const state = {
  levels: [],
  fullLevelsList: [],
  allWorkspacesLevels: [],
  users: {}, // used for mentions, { id: object }
  sorting: [],
  elementForEdit: null
}

const normalizeLevel = level => {
  return {
    ...level,
    [REQUEST_ENTITY_KEYS.WORKSPACE_IDS]: (level[OKR_LEVEL_ENTITY_KEYS.WORKSPACES] || []).map(
      ({ id }) => id
    )
  }
}

const levelsByWorkspaceId = new Map()

const getters = {
  getLevelItem:
    state =>
    (key, type, { workspaceId = null, typeId = null } = {}) => {
      const filterCriteria = level => level[key] === type

      const isSearchByWorkspace =
        !isNil(workspaceId) &&
        (typeId === OBJECTIVE_TYPES.TASK || typeId === OBJECTIVE_TYPES.WORK_ITEM)

      if (isSearchByWorkspace) {
        const levelInWorkspace = [...state.fullLevelsList, ...state.allWorkspacesLevels]
          .filter(filterCriteria)
          .find(level =>
            level[REQUEST_ENTITY_KEYS.WORKSPACE_IDS].some(id => Number(id) === Number(workspaceId))
          )

        if (levelInWorkspace) {
          return levelInWorkspace
        }
      }

      return (
        state.levels.find(filterCriteria) ||
        state.fullLevelsList.find(filterCriteria) ||
        state.allWorkspacesLevels.find(filterCriteria) || {
          [OKR_LEVEL_ENTITY_KEYS.FIELD_TYPE_IDS]: []
        }
      )
    },

  getSortingItem: state => column => state.sorting[column] || {},

  getLevelsByWorkspaceId: state => workspaceId => {
    if (levelsByWorkspaceId.has(workspaceId)) {
      return levelsByWorkspaceId.get(workspaceId)
    }

    const filterCriteria = level => level.workspaceIds.includes(workspaceId)

    let levels = state.fullLevelsList.filter(filterCriteria)

    if (isEmpty(levels)) {
      levels = state.allWorkspacesLevels.filter(filterCriteria)
    }

    levelsByWorkspaceId.set(workspaceId, levels)
    return levels
  }
}

const actions = {
  setElementForEdit({ commit }, element = null) {
    commit(SET_ELEMENT_FOR_EDIT, element)
  },

  clearElementForEdit({ dispatch }) {
    dispatch('setElementForEdit', null)
  },

  setLevels({ commit, dispatch }, { levels = {}, updateFullLevelsList = false } = {}) {
    commit(OBJECTIVES_SET_LEVELS, levels)
    if (updateFullLevelsList) {
      dispatch('setFullLevelsList', levels)
    }
  },

  async getUsersForMentioning({ commit, state }, { accountIds, searchString, limit, workspaceId }) {
    const unknownUsers = accountIds.filter(userId => !(userId in state.users))

    if (unknownUsers.length > 0) {
      const usersApi = new UsersApi()
      const users = await usersApi.getUsersForMentioning({
        accountIds: unknownUsers,
        searchString,
        limit,
        workspaceId
      })
      commit(OBJECTIVES_ADD_USERS, users)
    }

    return accountIds.reduce((acc, value) => ({ ...acc, [value]: state.users[value] }), {})
  },

  addUsersFromList({ commit }, users) {
    commit(OBJECTIVES_ADD_USERS, users)
  },
  updateSorting({ commit }, sorting) {
    commit(OBJECTIVES_SET_SORTING, sorting)
  },

  async setFullLevelsList({ commit }, levels) {
    commit(OBJECTIVES_SET_FULL_LEVELS_LIST, levels.map(normalizeLevel))
  },

  async setAllWorkspacesLevels({ commit }, levels) {
    commit(OBJECTIVES_SET_ALL_WORKSPACES_LEVELS, levels.map(normalizeLevel))
  },

  clearLevelsByWorkspaceIdCache() {
    if (levelsByWorkspaceId.size) {
      levelsByWorkspaceId.clear()
    }
  }
}

const mutations = {
  [OBJECTIVES_SET_LEVELS](state, levels) {
    state.levels = levels
  },
  [OBJECTIVES_ADD_USERS](state, users) {
    users.forEach(user => (state.users[user.accountId] = user))
  },
  [OBJECTIVES_SET_SORTING](state, sorting) {
    state.sorting = sorting
  },
  [OBJECTIVES_SET_FULL_LEVELS_LIST](state, levels) {
    state.fullLevelsList = levels
  },
  [OBJECTIVES_SET_ALL_WORKSPACES_LEVELS](state, levels) {
    // getLevelsByWorkspaceId memoizes its result per workspaceId and falls back to
    // allWorkspacesLevels when fullLevelsList has nothing for that workspace.
    // A call made before this list is loaded caches an empty array forever,
    // so every write here must drop the cache to let the fallback take effect.
    levelsByWorkspaceId.clear()

    state.allWorkspacesLevels = uniqBy(
      [...levels, ...state.allWorkspacesLevels],
      OKR_LEVEL_ENTITY_KEYS.ID
    )
  },
  [SET_ELEMENT_FOR_EDIT](state, element = null) {
    state.elementForEdit = element
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
